import { useEffect, useMemo, useState } from "react";

import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useUserConfiguration } from "~/shared/hooks/use-user-configuration.ts";
import type { Commodity } from "~/shared/schemas/commodity-schema.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import { useSavedCommodities } from "./use-commodities.ts";

export type CommodityWithDefault = Commodity & {
	isDefault: boolean;
};

export function useCommoditiesData(customerId: string, enabled = true) {
	const { employeeId } = useIsAuthenticated();
	const { isInternal } = useDeploymentType();
	const {
		userConfigs,
		isLoadingConfigs,
		createUserConfig,
		deleteUserConfig,
		isCreating,
		isUpdating,
		isDeleting,
	} = useUserConfiguration();

	const { data: quoteDetailsData, isLoading: isQuoteDetailsLoading } =
		useQuoteDetailsPageLoadQueries();
	const {
		data: savedCommodities,
		isLoading: isSavedCommoditiesLoading,
		isError: isSavedCommoditiesError,
		error: savedCommoditiesError,
		refetch,
	} = useSavedCommodities(customerId, enabled, isInternal);

	const [data, setData] = useState<CommodityWithDefault[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const commodityConfigsForCustomer = useMemo(() => {
		return userConfigs.filter(
			(config) =>
				config.configType === ConfigurationType.CommodityDefault &&
				config.customerId === Number(customerId),
		);
	}, [userConfigs, customerId]);

	useEffect(() => {
		if (savedCommodities?.savedCommodities) {
			const mappedData = savedCommodities.savedCommodities.map((commodity) => ({
				...commodity,
				isDefault: commodityConfigsForCustomer.some(
					(config) => config.configValue === String(commodity.id),
				),
			}));
			setData(mappedData);
		}
	}, [savedCommodities, commodityConfigsForCustomer]);

	useEffect(() => {
		setIsLoading(
			isQuoteDetailsLoading || isSavedCommoditiesLoading || isLoadingConfigs,
		);
	}, [isQuoteDetailsLoading, isSavedCommoditiesLoading, isLoadingConfigs]);

	return {
		data,
		setData,
		isLoading,
		setIsLoading,
		isError: isSavedCommoditiesError,
		error: savedCommoditiesError,
		refetch,
		unitTypes: quoteDetailsData?.unitTypes,
		freightClassData: quoteDetailsData?.freightClassData,
		createUserConfig,
		deleteUserConfig,
		userConfigs,
		employeeId,
		isConfigLoading: isCreating || isUpdating || isDeleting,
	};
}
